import { useState, useEffect, useCallback } from 'react';
import usePerimetre from '../hooks/usePerimetre';
import BandeauPerimetre from '../components/BandeauPerimetre';
import { siteEmploye } from '../utils/organisation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, AlertTriangle, Camera, Lock, Clipboard, Users, RefreshCw, X, AlertCircle, Search } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import ScannerQR from '../components/ScannerQR';

const VERT        = '#1B5E20';
const VERT_CLAIR  = '#E8F5E9';
const ROUGE       = '#C62828';
const ROUGE_CLAIR = '#FFEBEE';
const BLEU        = '#1565C0';
const BLEU_CLAIR  = '#E3F2FD';
const GRIS_BG     = '#FAFAFA';
const BORD        = '#E0E0E0';

const ROLES_RESPONSABLE = ['rh_principal', 'assistant_rh', 'responsable_site', 'chef_departement'];

const fmtHeure = (h) => (h ? h.slice(0, 5) : '—');
const fmtDate  = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR') : '—';

// ─── Composants utilitaires ─────────────────────────────────────────

const Card = ({ children, style = {} }) => (
  <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${BORD}`, padding: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', ...style }}>
    {children}
  </div>
);

const Badge = ({ children, bg, color }) => (
  <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: bg, color }}>
    {children}
  </span>
);

const Message = ({ msg }) => {
  if (!msg) return null;
  const ok = msg.type === 'succes';
  return (
    <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
      background: ok ? VERT_CLAIR : '#FFEBEE', color: ok ? VERT : ROUGE, border: `1px solid ${ok ? '#A5D6A7' : '#FFCDD2'}` }}>
      {ok ? <CheckCircle size={15} /> : <AlertTriangle size={15} />} {msg.texte}
    </div>
  );
};

// ─── Countdown circulaire ───────────────────────────────────────────

const Countdown = ({ secondes }) => {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const progres = (secondes / 30) * circ;
  const couleur = secondes <= 5 ? ROUGE : secondes <= 10 ? '#E65100' : VERT;

  return (
    <div style={{ position: 'relative', width: 120, height: 120 }}>
      <svg width="120" height="120" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="60" cy="60" r={r} fill="none" stroke="#E8F5E9" strokeWidth="6" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={couleur} strokeWidth="6"
          strokeDasharray={circ} strokeDashoffset={circ - progres}
          style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: couleur, lineHeight: 1 }}>{secondes}</div>
        <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginTop: 2 }}>sec</div>
      </div>
    </div>
  );
};

// Le composant Scanner QR (jsQR) est partagé avec la page de borne : components/ScannerQR.jsx.

// ─── Page principale ────────────────────────────────────────────────

export default function PointagePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { employe: authEmploye, hasPermission } = useAuth();
  const userStr  = localStorage.getItem('sogas_user') || localStorage.getItem('user') || '{}';
  const userLocal = (() => { try { return JSON.parse(userStr); } catch { return {}; } })();
  const user = authEmploye || userLocal;

  const estResponsableParPermissions = hasPermission('scanner_qr_code') || hasPermission('appel_nominal') || hasPermission('voir_subordonnes');
  const estResponsable = ROLES_RESPONSABLE.includes(user.role) || estResponsableParPermissions;

  const modeUrl = searchParams.get('mode'); // 'scanner' | 'appel-nominal' | null

  const [onglet, setOnglet]   = useState(estResponsable ? 'responsable' : 'employe');
  // Module 0 (clôture) : population réelle de chaque fonction, affichée à l'utilisateur.
  const { perimetre: perimetrePresents } = usePerimetre('scanner_qr_code');
  const { perimetre: perimetreAppel } = usePerimetre('appel_nominal');
  const [message, setMessage] = useState(null);

  // ── État onglet employé
  const [qrData, setQrData]             = useState(null);
  const [secondes, setSecondes]         = useState(30);
  const [pointageJour, setPointageJour] = useState(null);
  const [historique, setHistorique]     = useState([]);
  const [loadingQR, setLoadingQR]       = useState(false);

  // ── État onglet responsable
  const [afficherScanner, setAfficherScanner] = useState(false);
  // Chantier temps : le sens du passage est CHOISI avant le scan (jamais deviné).
  const [sensScan, setSensScan]               = useState('ARRIVEE');
  const [scanResultat, setScanResultat]       = useState(null);
  const [presents, setPresents]               = useState([]);
  const [totalActifs, setTotalActifs]         = useState(0);
  const [loadingPresents, setLoadingPresents] = useState(false);

  // ── Navigation vues RH (initialisé depuis ?mode= si présent)
  const [vueActive, setVueActive] = useState(
    modeUrl === 'scanner' ? 'scanner' :
    modeUrl === 'appel-nominal' ? 'appel' :
    null
  );
  const [rechercheAbsents, setRechercheAbsents] = useState('');

  // ── État appel nominal
  const [listeEmployes, setListeEmployes]   = useState([]);
  const [selectionAppel, setSelectionAppel] = useState({});
  const [typeAppel, setTypeAppel]           = useState('arrivee');
  const [motifAppel, setMotifAppel]         = useState('');
  const [loadingAppel, setLoadingAppel]     = useState(false);
  const [resultatAppel, setResultatAppel]   = useState(null);

  // ── Nombre d'employés en congé aujourd'hui (connecté au module Congés)
  const [nbEnConge, setNbEnConge] = useState(null);

  const showMsg = (type, texte) => {
    setMessage({ type, texte });
    setTimeout(() => setMessage(null), 5000);
  };

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('sogas_token')}` });

  // ── Chargement QR Code
  const chargerQR = useCallback(async () => {
    setLoadingQR(true);
    try {
      const { data } = await axios.get('/api/pointages/mon-qrcode', { headers: authHeader() });
      setQrData(data);
      setSecondes(data.secondesRestantes);
    } catch (e) {
      console.error('Erreur QR:', e);
    } finally {
      setLoadingQR(false);
    }
  }, []);

  // ── Chargement statut jour + historique
  const chargerStatut = useCallback(async () => {
    try {
      const [repJour, repHisto] = await Promise.all([
        axios.get('/api/pointages/aujourdhui', { headers: authHeader() }),
        axios.get('/api/pointages/mon-historique', { headers: authHeader() }),
      ]);
      setPointageJour(repJour.data.pointage);
      setHistorique(repHisto.data.pointages || []);
    } catch (e) {
      console.error('Erreur statut:', e);
    }
  }, []);

  // ── Chargement liste des présents
  const chargerPresents = useCallback(async () => {
    setLoadingPresents(true);
    try {
      const { data } = await axios.get('/api/pointages/presents', { headers: authHeader() });
      setPresents(data.pointages || []);
      setTotalActifs(data.totalActifs || 0);
    } catch (e) {
      console.error('Erreur présents:', e);
    } finally {
      setLoadingPresents(false);
    }
  }, []);

  // ── Nombre d'employés en congé aujourd'hui
  const chargerSyntheseJour = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/pointages/synthese-jour', { headers: authHeader() });
      setNbEnConge(data.nb_en_conge ?? 0);
    } catch (e) {
      setNbEnConge(0);
    }
  }, []);

  // ── Chargement liste employés pour appel nominal
  const chargerListeEmployes = useCallback(async () => {
    setLoadingAppel(true);
    try {
      const params = new URLSearchParams({ statut: 'Actif' });
      const posteId = authEmploye?.poste_organigramme?.id;
      if (estResponsableParPermissions && posteId) {
        params.append('poste_manager_id', posteId);
      }
      const { data } = await axios.get(`/api/employes?${params}`, { headers: authHeader() });
      setListeEmployes(data.employes || data || []);
    } catch (e) {
      console.error('Erreur liste employés:', e);
    } finally {
      setLoadingAppel(false);
    }
  }, []);

  // ── Valider appel nominal
  const validerAppelNominal = async () => {
    const employe_ids = Object.entries(selectionAppel).filter(([,v]) => v).map(([k]) => k);
    if (employe_ids.length === 0) { showMsg('erreur', 'Sélectionnez au moins un employé'); return; }
    if (motifAppel.trim().length < 5) { showMsg('erreur', 'Indiquez le motif de l\'appel nominal (par exemple : borne en panne).'); return; }
    setLoadingAppel(true);
    try {
      const { data } = await axios.post('/api/pointages/appel-nominal',
        { employe_ids, type: typeAppel, motif: motifAppel.trim() },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      setResultatAppel(data);
      setSelectionAppel({});
      showMsg('succes', data.message);
      chargerPresents();
    } catch (e) {
      showMsg('erreur', e.response?.data?.message || 'Erreur lors de l\'appel nominal');
    } finally {
      setLoadingAppel(false);
    }
  };

  // ── Initialisation
  useEffect(() => {
    chargerStatut();
    if (onglet === 'employe') chargerQR();
    if (onglet === 'responsable' && estResponsable) {
      chargerPresents();
      chargerListeEmployes();
      chargerSyntheseJour();
    }
  }, [onglet]);

  // ── Timer countdown QR
  useEffect(() => {
    if (onglet !== 'employe') return;
    const interval = setInterval(() => {
      setSecondes(s => { if (s <= 1) { chargerQR(); return 30; } return s - 1; });
    }, 1000);
    return () => clearInterval(interval);
  }, [onglet, chargerQR]);

  // ── Après scan réussi
  const apresScanner = async (payload) => {
    setAfficherScanner(false);
    try {
      const { data } = await axios.post('/api/pointages/scanner',
        { payload, sens: sensScan },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      setScanResultat({ ok: true, ...data });
      showMsg('succes', data.message);
      chargerPresents();
    } catch (e) {
      const err = e.response?.data;
      setScanResultat({ ok: false, message: err?.message || 'Erreur lors du scan', alerte: err?.alerte });
      showMsg('erreur', err?.message || 'QR Code invalide ou expiré');
    }
  };

  // ── JSX onglet employé (inchangé)
  const renderEmploye = () => {
    const aPointe = !!pointageJour?.heure_arrivee;
    const initiales = [user.prenom?.[0], user.nom?.[0]].filter(Boolean).join('').toUpperCase() || '?';
    const histo5 = historique.slice(0, 5);

    return (
      <>
        <Card style={{ textAlign: 'center', padding: '28px 24px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1a1a1a', marginBottom: 4 }}>Bonjour {user.prenom}</div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: `linear-gradient(135deg, ${VERT} 0%, #2E7D32 100%)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, margin: '0 auto 16px', boxShadow: '0 4px 16px rgba(27,94,32,0.35)' }}>
            {initiales}
          </div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>{user.prenom} {user.nom}</div>
          <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>{user.poste || '–'}{user.site ? ` · ${siteEmploye(user)}` : ''}</div>
          <div style={{ marginTop: 18, padding: '12px 16px', borderRadius: 10, display: 'inline-block',
            background: aPointe ? VERT_CLAIR : '#FFF8E1', border: `1.5px solid ${aPointe ? '#A5D6A7' : '#FFE082'}`,
            color: aPointe ? VERT : '#E65100', fontWeight: 700, fontSize: 13 }}>
            {aPointe ? `Arrivée pointée à ${fmtHeure(pointageJour.heure_arrivee)}` : 'Pas encore pointé aujourd\'hui'}
          </div>
        </Card>

        <Card style={{ textAlign: 'center', padding: '28px 24px' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#1a1a1a', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Lock size={15} />Mon QR Code de pointage</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>Présentez ce QR Code au responsable</div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            {loadingQR || !qrData ? (
              <div style={{ width: 200, height: 200, background: GRIS_BG, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `3px solid ${BORD}` }}><Camera size={40} color="#ccc" /></div>
            ) : (
              <img src={qrData.qrImage} alt="QR Code" style={{ width: 200, height: 200, borderRadius: 16, border: `3px solid ${VERT}`, boxShadow: '0 4px 20px rgba(27,94,32,0.2)', display: 'block' }} />
            )}
            <Countdown secondes={secondes} />
            <div style={{ fontSize: 12, color: '#888' }}>Le code se renouvelle automatiquement toutes les 30s</div>
          </div>
          <div style={{ marginTop: 20, padding: '12px 16px', background: '#FFF8E1', borderRadius: 10, fontSize: 12, color: '#E65100', border: '1px solid #FFE082', fontWeight: 600 }}>
            <AlertTriangle size={13} style={{ marginRight: 5, flexShrink: 0 }} />Code personnel — Ne partagez pas ce QR Code
          </div>
        </Card>

        <Card>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#333', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}><Clipboard size={15} />Mes 5 derniers pointages</div>
          {histo5.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb', fontSize: 13 }}>Aucun pointage enregistré</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${BORD}` }}>
                    {['Date','Arrivée','Départ','Durée','Statut'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'center', color: '#666', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {histo5.map((p, i) => {
                    const duree = (() => {
                      if (!p.heure_arrivee || !p.heure_depart) return '—';
                      const [h1,m1] = p.heure_arrivee.split(':').map(Number);
                      const [h2,m2] = p.heure_depart.split(':').map(Number);
                      const mins = (h2*60+m2) - (h1*60+m1);
                      if (mins < 0) return '—';
                      return `${Math.floor(mins/60)}h${String(mins%60).padStart(2,'0')}`;
                    })();
                    return (
                      <tr key={p.id} style={{ borderBottom: `1px solid #F5F5F5`, background: i%2===0 ? '#fff' : GRIS_BG }}>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 500 }}>{fmtDate(p.date)}</td>
                        <td style={{ padding: '10px', textAlign: 'center', color: VERT, fontWeight: 600 }}>{fmtHeure(p.heure_arrivee)}</td>
                        <td style={{ padding: '10px', textAlign: 'center', color: ROUGE, fontWeight: 600 }}>{fmtHeure(p.heure_depart)}</td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#555' }}>{duree}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <Badge bg={p.statut === 'present' ? VERT_CLAIR : '#FFEBEE'} color={p.statut === 'present' ? VERT : ROUGE}>
                            {{ present: 'Présent', absent: 'Absent', retard: 'En retard', conge: 'En congé' }[p.statut] || p.statut}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </>
    );
  };

  // ── Bouton retour (vues RH)
  const BoutonRetour = () => (
    <button
      onClick={() => setVueActive(null)}
      style={{ background: '#fff', border: `1px solid ${BORD}`, color: '#333', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600, marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      ← Retour
    </button>
  );

  // ── Dashboard RH — vue principale
  const renderDashboardRH = () => {
    const nbPresents = presents.length;
    const nbAbsents  = Math.max(0, totalActifs - nbPresents);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* 3 cards statistiques */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div onClick={() => setVueActive('presents')}
            style={{ background: VERT_CLAIR, borderRadius: 14, padding: '16px 10px', cursor: 'pointer', textAlign: 'center', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #A5D6A7' }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: VERT, lineHeight: 1 }}>{nbPresents}</div>
            <div style={{ fontSize: 11, color: '#2E7D32', fontWeight: 600, marginTop: 4 }}>Présents</div>
            <div style={{ fontSize: 10, color: VERT, marginTop: 8, opacity: 0.8 }}>Voir liste →</div>
          </div>

          <div onClick={() => setVueActive('total')}
            style={{ background: '#fff', borderRadius: 14, padding: '16px 10px', cursor: 'pointer', textAlign: 'center', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: `1px solid ${BORD}` }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: '#1a1a1a', lineHeight: 1 }}>{totalActifs}</div>
            <div style={{ fontSize: 11, color: '#555', fontWeight: 600, marginTop: 4 }}>Total actifs</div>
            <div style={{ fontSize: 10, color: '#888', marginTop: 8 }}>Voir liste →</div>
          </div>

          <div onClick={() => setVueActive('absents')}
            style={{ background: ROUGE_CLAIR, borderRadius: 14, padding: '16px 10px', cursor: 'pointer', textAlign: 'center', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1px solid #FFCDD2' }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: ROUGE, lineHeight: 1 }}>{nbAbsents}</div>
            <div style={{ fontSize: 11, color: ROUGE, fontWeight: 600, marginTop: 4 }}>Absents</div>
            <div style={{ fontSize: 10, color: ROUGE, marginTop: 8, opacity: 0.8 }}>Voir liste →</div>
          </div>
        </div>

        {/* 2 actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div onClick={() => setVueActive('scanner')}
            style={{ background: '#fff', borderRadius: 14, padding: '20px 14px', cursor: 'pointer', textAlign: 'center', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: `2px solid ${VERT}` }}>
            <div style={{ width: 46, height: 46, background: VERT_CLAIR, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <Camera size={22} color={VERT} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, color: VERT }}>Scanner QR</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Valider un pointage</div>
          </div>

          <div onClick={() => setVueActive('appel')}
            style={{ background: '#fff', borderRadius: 14, padding: '20px 14px', cursor: 'pointer', textAlign: 'center', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: `1px solid ${BORD}` }}>
            <div style={{ width: 46, height: 46, background: BLEU_CLAIR, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <Users size={22} color={BLEU} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#333' }}>Appel nominal</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Pointer en masse</div>
          </div>
        </div>
      </div>
    );
  };

  // ── Vue Présents
  const renderVuePresents = () => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <BoutonRetour />
        <button onClick={chargerPresents} style={{ background: '#fff', border: `1px solid ${BORD}`, color: '#555', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <RefreshCw size={12} />Actualiser
        </button>
      </div>
      <BandeauPerimetre perimetre={perimetrePresents} style={{ marginBottom: 10 }} />
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', flex: 1 }}>Présents aujourd'hui</div>
          <span style={{ background: VERT_CLAIR, color: VERT, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{presents.length} présents</span>
        </div>

        {loadingPresents ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: '#aaa' }}>Chargement...</div>
        ) : presents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: '#bbb', fontSize: 13 }}>Aucun pointage enregistré aujourd'hui</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {presents.map(p => {
              const emp = p.Employe || p.employe || {};
              const enPoste = p.heure_arrivee && !p.heure_depart;
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: GRIS_BG, borderRadius: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: VERT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {(emp.prenom?.[0] || '?')}{(emp.nom?.[0] || '')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.prenom} {emp.nom}</div>
                    <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.poste || '–'}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, color: VERT, fontWeight: 700 }}>{fmtHeure(p.heure_arrivee)}</div>
                    <div style={{ fontSize: 11, color: p.heure_depart ? ROUGE : '#888' }}>{p.heure_depart ? fmtHeure(p.heure_depart) : 'En cours'}</div>
                  </div>
                  <Badge bg={enPoste ? VERT_CLAIR : GRIS_BG} color={enPoste ? VERT : '#777'}>{enPoste ? 'Présent' : 'Complet'}</Badge>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );

  // ── Vue Absents
  const renderVueAbsents = () => {
    const presentsIds = new Set(presents.map(p => p.Employe?.id || p.employe?.id).filter(Boolean));
    const absents = listeEmployes.filter(emp => !presentsIds.has(emp.id));
    const filtres = rechercheAbsents
      ? absents.filter(e => `${e.prenom} ${e.nom}`.toLowerCase().includes(rechercheAbsents.toLowerCase()))
      : absents;

    return (
      <div>
        <BoutonRetour />
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', flex: 1 }}>Absents aujourd'hui</div>
            <span style={{ background: ROUGE_CLAIR, color: ROUGE, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{absents.length} absents</span>
          </div>

          <div style={{ position: 'relative', marginBottom: 14 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
            <input
              type="text"
              placeholder="Rechercher par nom..."
              value={rechercheAbsents}
              onChange={e => setRechercheAbsents(e.target.value)}
              style={{ width: '100%', padding: '8px 10px 8px 30px', borderRadius: 8, border: `1px solid ${BORD}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {filtres.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb', fontSize: 13 }}>Aucun absent trouvé</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtres.map(emp => (
                <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: GRIS_BG, borderRadius: 10, borderLeft: `4px solid ${ROUGE}` }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: ROUGE_CLAIR, color: ROUGE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, border: '2px solid #FFCDD2' }}>
                    {(emp.prenom?.[0] || '?')}{(emp.nom?.[0] || '')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.prenom} {emp.nom}</div>
                    <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[emp.poste, emp.site && siteEmploye(emp)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <Badge bg={ROUGE_CLAIR} color={ROUGE}>Non pointé</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    );
  };

  // ── Vue Total
  const renderVueTotal = () => {
    const presentsIds = new Set(presents.map(p => p.Employe?.id || p.employe?.id).filter(Boolean));
    const nbPresents  = presents.length;
    const nbAbsents   = Math.max(0, totalActifs - nbPresents);

    return (
      <div>
        <BoutonRetour />
        <Card>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 14 }}>Tous les employés actifs</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
            <div style={{ background: VERT_CLAIR, borderRadius: 10, padding: '10px 6px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: VERT }}>{nbPresents}</div>
              <div style={{ fontSize: 10, color: '#2E7D32', fontWeight: 600 }}>Présents</div>
            </div>
            <div style={{ background: '#FFF3E0', borderRadius: 10, padding: '10px 6px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#E65100' }}>{nbEnConge ?? '—'}</div>
              <div style={{ fontSize: 10, color: '#E65100', fontWeight: 600 }}>En congé</div>
            </div>
            <div style={{ background: ROUGE_CLAIR, borderRadius: 10, padding: '10px 6px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: ROUGE }}>{nbAbsents}</div>
              <div style={{ fontSize: 10, color: ROUGE, fontWeight: 600 }}>Absents</div>
            </div>
          </div>

          {listeEmployes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb', fontSize: 13 }}>Chargement...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {listeEmployes.map(emp => {
                const ptJour  = presents.find(p => (p.Employe?.id || p.employe?.id) === emp.id);
                const aPointe = !!ptJour?.heure_arrivee;
                const aParti  = !!ptJour?.heure_depart;
                const labelBadge = aParti ? 'Complet' : aPointe ? 'Présent' : 'Absent';
                const bgBadge    = aPointe ? VERT_CLAIR : ROUGE_CLAIR;
                const colorBadge = aPointe ? VERT : ROUGE;

                return (
                  <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: GRIS_BG, borderRadius: 10, borderLeft: `3px solid ${aPointe ? VERT : ROUGE}` }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: aPointe ? VERT : '#ccc', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                      {(emp.prenom?.[0] || '?')}{(emp.nom?.[0] || '')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.prenom} {emp.nom}</div>
                      <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.poste || '–'}</div>
                    </div>
                    {aPointe && <div style={{ fontSize: 11, color: VERT, fontWeight: 600, flexShrink: 0 }}>{fmtHeure(ptJour.heure_arrivee)}</div>}
                    <Badge bg={bgBadge} color={colorBadge}>{labelBadge}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    );
  };

  // ── Section scanner (fonctionnalité inchangée)
  const renderScannerSection = () => (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#333', display: 'flex', alignItems: 'center', gap: 6 }}><Camera size={15} />Valider un pointage</div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Choisissez le sens, puis scannez le QR Code de l'employé</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {[{ v: 'ARRIVEE', label: 'Arrivée' }, { v: 'DEPART', label: 'Départ' }, { v: 'DEBUT_PAUSE', label: 'Début de pause' }, { v: 'FIN_PAUSE', label: 'Fin de pause' }].map(t => (
              <button key={t.v} type="button" onClick={() => setSensScan(t.v)} aria-pressed={sensScan === t.v}
                style={{ padding: '6px 12px', borderRadius: 8, border: `2px solid ${sensScan === t.v ? VERT : BORD}`, background: sensScan === t.v ? VERT_CLAIR : '#fff', color: sensScan === t.v ? VERT : '#666', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <button onClick={() => { setScanResultat(null); setAfficherScanner(true); }}
          style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Camera size={16} />Scanner un QR Code
        </button>
      </div>

      {scanResultat && (
        <div style={{ marginTop: 16, padding: 16, borderRadius: 10, border: `2px solid ${scanResultat.ok ? '#A5D6A7' : '#FFCDD2'}`, background: scanResultat.ok ? VERT_CLAIR : '#FFEBEE' }}>
          {scanResultat.ok ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: VERT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, flexShrink: 0 }}>
                {(scanResultat.employe?.prenom?.[0] || '?')}{(scanResultat.employe?.nom?.[0] || '')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: VERT }}>{scanResultat.employe?.prenom} {scanResultat.employe?.nom}</div>
                <div style={{ fontSize: 12, color: '#555' }}>{scanResultat.employe?.matricule} · {scanResultat.employe?.poste || 'N/A'}</div>
              </div>
              <Badge bg={scanResultat.action === 'arrivee' ? VERT_CLAIR : '#FFEBEE'} color={scanResultat.action === 'arrivee' ? VERT : ROUGE}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: scanResultat.action === 'arrivee' ? VERT : ROUGE, display: 'inline-block' }} />
                  {({ arrivee: 'Arrivée', depart: 'Départ', debut_pause: 'Début de pause', fin_pause: 'Fin de pause' })[scanResultat.action] || 'Passage'}{scanResultat.heure ? ` · ${scanResultat.heure}` : ''}
                </span>
              </Badge>
              {(scanResultat.information || scanResultat.doublon) && (
                <div style={{ flexBasis: '100%', fontSize: 13, color: '#E65100', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={14} />{scanResultat.information || scanResultat.message}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {scanResultat.alerte ? <AlertCircle size={24} color={ROUGE} /> : <X size={24} color={ROUGE} />}
              <div>
                <div style={{ fontWeight: 700, color: ROUGE }}>{scanResultat.alerte ? 'ALERTE FRAUDE DÉTECTÉE' : 'Échec du pointage'}</div>
                <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>{scanResultat.message}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  // ── Appel nominal (fonctionnalité inchangée)
  const renderAppelNominal = () => {
    const nbSelectionnes       = Object.values(selectionAppel).filter(Boolean).length;
    const nbPresentsAujourdhui = presents.filter(p => !p.heure_depart).length;

    const toggleTous = (val) => {
      const s = {};
      listeEmployes.forEach(e => { s[e.id] = val; });
      setSelectionAppel(s);
    };

    return (
      <>
        <BandeauPerimetre perimetre={perimetreAppel} style={{ marginBottom: 10 }} />
        <Card>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <div style={{ background: VERT_CLAIR, borderRadius: 12, padding: '16px 24px', textAlign: 'center', flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: VERT }}>{nbPresentsAujourdhui}</div>
              <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', marginTop: 4 }}>Présents</div>
            </div>
            <div style={{ background: GRIS_BG, borderRadius: 12, padding: '16px 24px', textAlign: 'center', flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#555' }}>{totalActifs}</div>
              <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', marginTop: 4 }}>Total actifs</div>
            </div>
            <div style={{ background: BLEU_CLAIR, borderRadius: 12, padding: '16px 24px', textAlign: 'center', flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: BLEU }}>{totalActifs - nbPresentsAujourdhui}</div>
              <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', marginTop: 4 }}>Absents</div>
            </div>
          </div>
        </Card>

        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{ v: 'arrivee', label: 'Arrivée', dot: VERT }, { v: 'depart', label: 'Départ', dot: ROUGE }].map(t => (
                <button key={t.v} onClick={() => setTypeAppel(t.v)}
                  style={{ padding: '8px 18px', borderRadius: 8, border: `2px solid ${typeAppel === t.v ? (t.v === 'arrivee' ? VERT : ROUGE) : BORD}`, background: typeAppel === t.v ? (t.v === 'arrivee' ? VERT_CLAIR : '#FFEBEE') : '#fff', color: typeAppel === t.v ? (t.v === 'arrivee' ? VERT : ROUGE) : '#666', fontWeight: 700, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.dot, display: 'inline-block' }} />{t.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => toggleTous(true)} style={{ background: 'none', border: `1px solid ${BORD}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>Tout sélectionner</button>
              <button onClick={() => toggleTous(false)} style={{ background: 'none', border: `1px solid ${BORD}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>Tout désélectionner</button>
              <button onClick={validerAppelNominal} disabled={loadingAppel || nbSelectionnes === 0}
                style={{ background: nbSelectionnes > 0 ? VERT : '#ccc', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: nbSelectionnes > 0 ? 'pointer' : 'default', fontWeight: 700, fontSize: 13 }}>
                {loadingAppel ? '...' : `Valider (${nbSelectionnes})`}
              </button>
            </div>
          </div>

          <label style={{ display: 'block', fontSize: 12, color: '#555', fontWeight: 600, marginBottom: 12 }}>
            Motif de l'appel nominal (obligatoire)
            <input type="text" value={motifAppel} onChange={e => setMotifAppel(e.target.value)} maxLength={300}
              placeholder="Borne en panne, réseau coupé…"
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: `1px solid ${BORD}`, fontSize: 13 }} />
          </label>

          {resultatAppel && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: VERT_CLAIR, borderRadius: 8, color: VERT, fontWeight: 600, fontSize: 13, border: '1px solid #A5D6A7' }}>
              {resultatAppel.message}
              {(resultatAppel.resultats || []).filter(r => r.information || r.doublon || !r.succes).map(r => (
                <div key={r.employe_id} style={{ fontWeight: 500, color: r.succes ? '#E65100' : ROUGE, marginTop: 4 }}>
                  {r.information || r.message}
                </div>
              ))}
              <button onClick={() => setResultatAppel(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#666', display: 'inline-flex', alignItems: 'center' }}><X size={14} /></button>
            </div>
          )}

          {loadingAppel ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#aaa' }}>Chargement...</div>
          ) : (
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {listeEmployes.map((emp, i) => {
                const ptJour = presents.find(p => (p.Employe?.id || p.employe?.id) === emp.id);
                const aPointe = !!ptJour?.heure_arrivee;
                const aParti  = !!ptJour?.heure_depart;
                return (
                  <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px', borderBottom: `1px solid ${i < listeEmployes.length-1 ? GRIS_BG : 'transparent'}`, borderRadius: 8, background: selectionAppel[emp.id] ? (typeAppel === 'arrivee' ? '#F1F8E9' : '#FFF3E0') : 'transparent' }}>
                    <input type="checkbox" checked={!!selectionAppel[emp.id]} onChange={e => setSelectionAppel(prev => ({ ...prev, [emp.id]: e.target.checked }))}
                      style={{ width: 18, height: 18, cursor: 'pointer', accentColor: typeAppel === 'arrivee' ? VERT : ROUGE }} />
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: VERT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                      {(emp.prenom?.[0] || '?')}{(emp.nom?.[0] || '')}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{emp.prenom} {emp.nom}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{emp.poste || '–'} · {siteEmploye(emp)}</div>
                    </div>
                    <div style={{ fontSize: 11, textAlign: 'right' }}>
                      {aParti
                        ? <Badge bg={GRIS_BG} color="#777">Parti {fmtHeure(ptJour.heure_depart)}</Badge>
                        : aPointe
                          ? <Badge bg={VERT_CLAIR} color={VERT}>Arrivée {fmtHeure(ptJour.heure_arrivee)}</Badge>
                          : <Badge bg="#FFF8E1" color="#E65100">Non pointé</Badge>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </>
    );
  };

  // ── Rendu responsable — dispatche vers la vue active
  const renderResponsable = () => {
    if (vueActive === 'presents') return renderVuePresents();
    if (vueActive === 'absents')  return renderVueAbsents();
    if (vueActive === 'total')    return renderVueTotal();
    if (vueActive === 'scanner')  return <><BoutonRetour />{renderScannerSection()}</>;
    if (vueActive === 'appel')    return <><BoutonRetour />{renderAppelNominal()}</>;
    return renderDashboardRH();
  };

  // ─── Rendu principal ──────────────────────────────────────────────

  const aujourd_hui = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  if (!estResponsable) {
    const initiales = `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}`.toUpperCase() || '?';
    return (
      <div style={{ minHeight: '100vh', background: '#f7f7f7', display: 'flex', flexDirection: 'column', maxWidth: 480, margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif', overflowX: 'hidden' }}>
        <div style={{ background: VERT, padding: '14px 18px 20px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, position: 'relative', zIndex: 1 }}>
            <img src="/logo-sogas1.png" alt="SOGAS" style={{ height: 28, objectFit: 'contain', display: 'block' }}
              onError={(e) => { e.target.onerror = null; e.target.src = '/logo-sogas.png'; }} />
            <button onClick={() => navigate('/accueil-employe')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}>← Accueil</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
            <div style={{ width: 44, height: 44, background: 'rgba(255,255,255,0.2)', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.9rem', flexShrink: 0 }}>{initiales}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.prenom} {user.nom}</div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[user.poste, user.site && siteEmploye(user), user.matricule].filter(Boolean).join(' · ')}</div>
            </div>
            <span style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '3px 10px', color: '#fff', fontSize: '0.62rem', fontWeight: 600, flexShrink: 0 }}>En poste</span>
          </div>
        </div>
        <div style={{ flex: 1, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Message msg={message} />
          {renderEmploye()}
        </div>
      </div>
    );
  }

  const labelRole = estResponsableParPermissions
    ? (user?.poste_organigramme?.intitule || 'Responsable')
    : ({
        rh_principal: 'RH Principal', assistant_rh: 'Assistant RH',
        responsable_site: 'Resp. Site', chef_departement: 'Chef Dépt',
      }[user.role] || user.role || '');

  return (
    <Layout>
      <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', minHeight: '100vh', background: '#f7f7f7' }}>

        {/* ══ HEADER VERT COMPACT ══ */}
        <div className="sg-bandeau-clair" style={{ background: VERT, padding: '14px 18px 20px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, position: 'relative', zIndex: 1 }}>
            <img src="/logo-sogas1.png" alt="SOGAS" style={{ height: 28, objectFit: 'contain', display: 'block' }}
              onError={e => { e.target.onerror = null; e.target.src = '/logo-sogas.png'; }} />
            <NotificationBell couleurIcone="#fff" taille={18} />
          </div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <p style={{ margin: '0 0 2px 0', color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>Pointage SOGAS</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.65)', fontSize: '0.65rem', textTransform: 'capitalize' }}>{aujourd_hui}</p>
              {labelRole && <span style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '2px 8px', color: '#fff', fontSize: '0.58rem', fontWeight: 600 }}>{labelRole}</span>}
            </div>
          </div>
        </div>

        {/* ══ CORPS ══ */}
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Onglets Mon QR / Tableau RH — masqués si une vue est active */}
          {estResponsable && !vueActive && (
            <div style={{ display: 'flex', background: '#fff', borderRadius: 12, border: '1px solid #eee', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              {[
                { k: 'employe',     label: 'Mon QR Code',  desc: 'Afficher mon code' },
                { k: 'responsable', label: 'Tableau RH',   desc: 'Gérer les pointages' },
              ].map((o, idx) => (
                <button key={o.k} onClick={() => setOnglet(o.k)}
                  style={{ flex: 1, padding: '11px 10px', border: 'none', cursor: 'pointer', textAlign: 'center', background: onglet === o.k ? VERT : 'transparent', color: onglet === o.k ? '#fff' : '#666', borderRight: idx === 0 ? '1px solid #eee' : 'none', transition: 'all 0.15s' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem' }}>{o.label}</div>
                  <div style={{ fontSize: '0.65rem', opacity: 0.8, marginTop: 2 }}>{o.desc}</div>
                </button>
              ))}
            </div>
          )}

          <Message msg={message} />

          {onglet === 'employe'     && renderEmploye()}
          {onglet === 'responsable' && estResponsable && renderResponsable()}

        </div>
      </div>

      {afficherScanner && <ScannerQR onScan={apresScanner} onFermer={() => setAfficherScanner(false)} />}
    </Layout>
  );
}
